import React from 'react';
import PropTypes from 'prop-types';
import { Button, Dropdown } from '@gasbuddy/react-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faArrowRight } from '@fortawesome/free-solid-svg-icons';

import BpUtils from '../../../../lib/bputils';

import styles from './styles.css';

const NUM_ROWS = [
  10,
  25,
  50,
  100,
];

export default function Paginator({
  pageSize: pageSizeProps,
  pageNumber: pageNumberProps,
  numberOfEntries,
  onChange,
  minWidth,
}) {
  const [pageSize, setPageSize] = React.useState(pageSizeProps);
  const [pageNumber, setPageNumber] = React.useState(pageNumberProps);
  const [maxPageSize, setMaxPageSize] = React.useState(Math.ceil(numberOfEntries / pageSizeProps));

  React.useEffect(() => {
    const queryParams = BpUtils.getQueryParameters();
    if (queryParams?.pageNumber && queryParams?.pageSize) {
      const pNumber = Number(queryParams.pageNumber);
      const pSize = Number(queryParams.pageSize);
      setPageNumber(pNumber);
      setPageSize(pSize);
    }
  }, []);

  React.useEffect(() => {
    setMaxPageSize(Math.ceil(numberOfEntries / pageSizeProps));
  }, [numberOfEntries, pageSizeProps]);

  React.useEffect(() => {
    if (onChange) {
      if (pageNumber && pageSize) {
        BpUtils.setQueryParameter('pageNumber', pageNumber);
        BpUtils.setQueryParameter('pageSize', pageSize);
      }
      onChange({ pageNumber, pageSize });
    }
  }, [pageNumber, pageSize]);

  const getUpperRowCount = React.useCallback(() => {
    const upperRowCount = pageSize * pageNumber;
    return upperRowCount > numberOfEntries ? numberOfEntries : upperRowCount;
  }, [pageSize, pageNumber, numberOfEntries]);

  const handleOnPageSizeChange = ({ target: { value } }) => {
    if (value) {
      setPageSize(Number(value));
      setPageNumber(1);
    }
  };

  const goToFirstPage = () => setPageNumber(1);

  const increasePageNumber = () => setPageNumber(pageNumber + 1);

  const decreasePageNumber = () => setPageNumber(pageNumber - 1);

  const goToLastPage = () => setPageNumber(maxPageSize);

  return (
    <div className={styles.paginator} style={minWidth ? { minWidth: `${minWidth}px` } : {}}>
      <div className={styles.displayFlex}>
        <div className={styles.pageSizeSelectorLabel}>Rows per page</div>
        <div className={styles.rowsDropdownContainer}>
          <Dropdown
            size="sm"
            className={styles.rowsDropdown}
            options={NUM_ROWS.map(val => ({ label: val, value: val }))}
            defaultValue={NUM_ROWS[1]}
            onChange={handleOnPageSizeChange}
            value={pageSize}
          />
        </div>
      </div>
      <div>{`${(pageSize * pageNumber) - pageSize + 1}-${getUpperRowCount()} of ${numberOfEntries}`}</div>
      <div className={styles.displayFlex}>
        <Button className={styles.paginatorButton} disabled={pageNumber === 1} onClick={goToFirstPage}>
          <div className={styles.displayFlex}>
            <div className={styles.arrowVerticalLine}>&nbsp;</div>
            <FontAwesomeIcon icon={faArrowLeft} />
          </div>
        </Button>
        <Button className={styles.paginatorButton} disabled={pageNumber === 1} onClick={decreasePageNumber}>
          <FontAwesomeIcon icon={faArrowLeft} />
        </Button>
        <Button className={styles.paginatorButton} disabled={pageNumber === maxPageSize} onClick={increasePageNumber}>
          <FontAwesomeIcon icon={faArrowRight} />
        </Button>
        <Button className={styles.paginatorButton} disabled={pageNumber === maxPageSize} onClick={goToLastPage}>
          <div style={{ display: 'flex' }}>
            <FontAwesomeIcon icon={faArrowRight} />
            <div className={`${styles.arrowVerticalLine} ${styles.rightArrowVerticalLine}`}>&nbsp;</div>
          </div>
        </Button>
      </div>
    </div>
  );
}

Paginator.propTypes = {
  pageSize: PropTypes.number,
  pageNumber: PropTypes.number,
  numberOfEntries: PropTypes.number,
  onChange: PropTypes.func,
  minWidth: PropTypes.number,
};

Paginator.defaultProps = {
  pageSize: NUM_ROWS[1],
  pageNumber: 1,
  numberOfEntries: null,
  onChange: () => { },
  minWidth: null,
};
